import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Loader2, ArrowLeft, Plus, X, ChevronDown, Upload } from 'lucide-react'

export default function CreateParish() {
  const navigate = useNavigate()
  const { isSuperAdmin } = useUserRole()
  const [error, setError] = useState(null)
  const [newParishData, setNewParishData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    email: '',
    phone: '',
    description: '',
    briefBio: '',
    orgType: 'parish',
    legalName: '',
    ein: '',
    fundUseText: '',
    fundUseUrl: '',
  })
  const [verificationDocs, setVerificationDocs] = useState({
    irs_ein_letter: null,
    diocesan_inclusion_letter: null,
    other: null,
  })
  const [selectedAdmins, setSelectedAdmins] = useState([]) // Array of user IDs
  const [users, setUsers] = useState([]) // All users for admin selection
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [creating, setCreating] = useState(false)
  const [adminSearchQuery, setAdminSearchQuery] = useState('')
  const [showAdminDropdown, setShowAdminDropdown] = useState(false)
  const adminDropdownRef = useRef(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)

  useEffect(() => {
    if (!isSuperAdmin) {
      setError('You do not have permission to access this page.')
      return
    }
    fetchUsers()
  }, [isSuperAdmin])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target)) {
        setShowAdminDropdown(false)
      }
    }

    if (showAdminDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showAdminDropdown])

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true)
      const usersData = await api.getAllUsers()
      setUsers(usersData || [])
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError('Failed to load users. Please refresh the page.')
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleNewParishInputChange = (field, value) => {
    setNewParishData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleToggleAdmin = (userId) => {
    setSelectedAdmins(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId)
      } else {
        return [...prev, userId]
      }
    })
  }

  const handleRemoveAdmin = (userId) => {
    setSelectedAdmins(prev => prev.filter(id => id !== userId))
  }

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    if (!adminSearchQuery.trim()) return true
    const query = adminSearchQuery.toLowerCase()
    const email = (user.email || '').toLowerCase()
    const name = (user.name || '').toLowerCase()
    return email.includes(query) || name.includes(query)
  })

  // Get selected user objects
  const selectedAdminUsers = users.filter(user => selectedAdmins.includes(user.id))

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, WebP, or GIF image.')
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB. Please select a smaller image.')
      return
    }

    setSelectedImage(file)
    setError(null)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
  }

  const handleCreateParish = async () => {
    try {
      setCreating(true)
      setError(null)

      // Validate required fields
      if (!newParishData.name || !newParishData.address || !newParishData.city || 
          !newParishData.state || !newParishData.zipCode || !newParishData.email) {
        setError('Please fill in all required fields (Name, Address, City, State, ZIP Code, Email)')
        setCreating(false)
        return
      }

      // Validate state format (2 uppercase letters)
      if (!/^[A-Z]{2}$/.test(newParishData.state)) {
        setError('State must be a 2-letter uppercase code (e.g., NY, CA)')
        setCreating(false)
        return
      }

      // Validate ZIP code format
      if (!/^\d{5}(-\d{4})?$/.test(newParishData.zipCode)) {
        setError('ZIP code must be in format 12345 or 12345-6789')
        setCreating(false)
        return
      }

      // Prepare parish data
      const parishPayload = {
        name: newParishData.name,
        address: newParishData.address,
        city: newParishData.city,
        state: newParishData.state.toUpperCase(),
        zipCode: newParishData.zipCode,
        email: newParishData.email,
        phone: newParishData.phone || undefined,
        description: newParishData.description || undefined,
        briefBio: newParishData.briefBio || undefined,
        orgType: newParishData.orgType,
        legalName: newParishData.legalName.trim() || undefined,
        ein: newParishData.ein.trim() || undefined,
        fundUseText: newParishData.fundUseText.trim() || undefined,
        fundUseUrl: newParishData.fundUseUrl.trim() || undefined,
      }

      // Create the parish
      const createdParish = await api.createParish(parishPayload)

      // Upload image if one was selected
      if (selectedImage) {
        try {
          setUploadingImage(true)
          await api.uploadParishImage(createdParish.id, selectedImage)
        } catch (err) {
          console.error('Failed to upload image:', err)
          // Show warning but don't fail the whole operation
          setError(`Parish created successfully, but failed to upload image: ${err.message}`)
        } finally {
          setUploadingImage(false)
        }
      }

      // Upload any verification documents that were selected
      const docLabels = {
        irs_ein_letter: 'IRS EIN letter',
        diocesan_inclusion_letter: 'Diocesan inclusion letter',
        other: 'Other document',
      }
      const failedDocs = []
      for (const [kind, file] of Object.entries(verificationDocs)) {
        if (!file) continue
        try {
          await api.uploadVerificationDocument(createdParish.id, file, kind)
        } catch (err) {
          console.error('Failed to upload verification document:', err)
          failedDocs.push(`${docLabels[kind] || kind} (${err.message})`)
        }
      }
      if (failedDocs.length > 0) {
        setError(`Parish created successfully, but these documents failed to upload: ${failedDocs.join('; ')}`)
      }

      // Assign parish admin roles to selected users
      if (selectedAdmins.length > 0) {
        try {
          await Promise.all(
            selectedAdmins.map(async (userId) => {
              // Get current user roles
              const userRolesData = await api.getUserRoles(userId)
              const currentRoles = (userRolesData.roles || []).map(role => ({
                roleName: role.name,
                parishId: role.parishId || null
              }))

              // Add parish-admin role for the new parish
              const updatedRoles = [
                ...currentRoles,
                {
                  roleName: 'parish-admin',
                  parishId: createdParish.id
                }
              ]

              // Update user roles
              await api.updateUserRoles(userId, updatedRoles)
            })
          )
        } catch (err) {
          console.error('Failed to assign admin roles:', err)
          // Show warning but don't fail the whole operation
          setError(`Parish created successfully, but failed to assign some admin roles: ${err.message}`)
        }
      }

      // Navigate back to parishes list
      navigate('/app/admin/parishes')
    } catch (err) {
      console.error('Failed to create parish:', err)
      setError(err.message || 'Failed to create parish')
    } finally {
      setCreating(false)
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-6">
        <p className="text-red-600">You do not have permission to access this page.</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/app/admin/parishes')}
            className="text-charcoal hover:text-charcoal-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-display text-charcoal mb-2">Create New Parish</h1>
            <p className="text-charcoal-muted">Add a new parish to the system</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 mb-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <div className="bg-white border border-surface-border rounded p-6">
        <div className="space-y-6">
          {/* Required Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-charcoal-muted mb-1">Name *</Label>
              <Input
                value={newParishData.name}
                onChange={(e) => handleNewParishInputChange('name', e.target.value)}
                placeholder="St. Mary's Catholic Church"
              />
            </div>
            <div>
              <Label className="text-charcoal-muted mb-1">Email *</Label>
              <Input
                type="email"
                value={newParishData.email}
                onChange={(e) => handleNewParishInputChange('email', e.target.value)}
                placeholder="info@stmarys.org"
              />
            </div>
            <div>
              <Label className="text-charcoal-muted mb-1">Address *</Label>
              <Input
                value={newParishData.address}
                onChange={(e) => handleNewParishInputChange('address', e.target.value)}
                placeholder="123 Main Street"
              />
            </div>
            <div>
              <Label className="text-charcoal-muted mb-1">City *</Label>
              <Input
                value={newParishData.city}
                onChange={(e) => handleNewParishInputChange('city', e.target.value)}
                placeholder="New York"
              />
            </div>
            <div>
              <Label className="text-charcoal-muted mb-1">State *</Label>
              <Input
                value={newParishData.state}
                onChange={(e) => handleNewParishInputChange('state', e.target.value.toUpperCase())}
                placeholder="NY"
                maxLength={2}
              />
              <p className="text-xs text-charcoal-muted mt-1">2-letter code (e.g., NY, CA)</p>
            </div>
            <div>
              <Label className="text-charcoal-muted mb-1">ZIP Code *</Label>
              <Input
                value={newParishData.zipCode}
                onChange={(e) => handleNewParishInputChange('zipCode', e.target.value)}
                placeholder="10001"
              />
              <p className="text-xs text-charcoal-muted mt-1">Format: 12345 or 12345-6789</p>
            </div>
            <div>
              <Label className="text-charcoal-muted mb-1">Phone</Label>
              <Input
                value={newParishData.phone}
                onChange={(e) => handleNewParishInputChange('phone', e.target.value)}
                placeholder="+1-555-123-4567"
              />
            </div>
          </div>

          {/* Optional Fields */}
          <div>
            <Label className="text-charcoal-muted mb-1">Brief Bio</Label>
            <textarea
              value={newParishData.briefBio}
              onChange={(e) => handleNewParishInputChange('briefBio', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
              rows="2"
              placeholder="A vibrant Catholic community in the heart of downtown."
              maxLength={500}
            />
            <p className="text-xs text-charcoal-muted mt-1">{newParishData.briefBio.length}/500 characters</p>
          </div>

          <div>
            <Label className="text-charcoal-muted mb-1">Description</Label>
            <textarea
              value={newParishData.description}
              onChange={(e) => handleNewParishInputChange('description', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
              rows="4"
              placeholder="A welcoming Catholic community serving the downtown area since 1850."
              maxLength={2000}
            />
            <p className="text-xs text-charcoal-muted mt-1">{newParishData.description.length}/2000 characters</p>
          </div>

          {/* Organization verification */}
          <div className="space-y-4">
            <div>
              <Label className="text-charcoal-muted mb-1" htmlFor="create-org-type">Organization Type</Label>
              <select
                id="create-org-type"
                value={newParishData.orgType}
                onChange={(e) => handleNewParishInputChange('orgType', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value="parish">Parish</option>
                <option value="catholic_center">Catholic center</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-charcoal-muted mb-1" htmlFor="create-legal-name">Legal Name</Label>
                <Input
                  id="create-legal-name"
                  value={newParishData.legalName}
                  onChange={(e) => handleNewParishInputChange('legalName', e.target.value)}
                  maxLength={200}
                  className="bg-white border-surface-border text-charcoal"
                />
              </div>
              <div>
                <Label className="text-charcoal-muted mb-1" htmlFor="create-ein">EIN</Label>
                <Input
                  id="create-ein"
                  value={newParishData.ein}
                  onChange={(e) => handleNewParishInputChange('ein', e.target.value)}
                  placeholder="12-3456789"
                  className="bg-white border-surface-border text-charcoal"
                />
              </div>
            </div>
            <div>
              <Label className="text-charcoal-muted mb-1" htmlFor="create-fund-use-text">How donations are used</Label>
              <textarea
                id="create-fund-use-text"
                value={newParishData.fundUseText}
                onChange={(e) => handleNewParishInputChange('fundUseText', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
                rows="3"
                maxLength={1000}
              />
            </div>
            <div>
              <Label className="text-charcoal-muted mb-1" htmlFor="create-fund-use-url">Link to more details</Label>
              <Input
                id="create-fund-use-url"
                type="url"
                value={newParishData.fundUseUrl}
                onChange={(e) => handleNewParishInputChange('fundUseUrl', e.target.value)}
                placeholder="https://"
                className="bg-white border-surface-border text-charcoal"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-charcoal-muted">Verification documents (optional, PDF, JPG or PNG)</Label>
              {[
                ['irs_ein_letter', 'IRS EIN letter'],
                ['diocesan_inclusion_letter', 'Diocesan inclusion letter'],
                ['other', 'Other'],
              ].map(([kind, label]) => (
                <div key={kind} className="flex items-center gap-3 text-sm">
                  <label htmlFor={`create-doc-${kind}`} className="w-52 text-charcoal">{label}</label>
                  <input
                    id={`create-doc-${kind}`}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setVerificationDocs((prev) => ({ ...prev, [kind]: e.target.files?.[0] || null }))
                    }
                    className="text-sm text-charcoal"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Image Upload Section */}
          <div>
            <Label className="text-charcoal-muted mb-2">Parish Image</Label>
            <div className="space-y-4">
              {/* Image Preview */}
              {imagePreview && (
                <div className="relative w-full max-w-md">
                  <img
                    src={imagePreview}
                    alt="Parish preview"
                    className="w-full h-48 object-cover rounded border border-surface-border"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                  {selectedImage && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 text-xs bg-gold/90 text-black rounded">
                        New Image
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              {/* File Input and Upload Button */}
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="image-input-create"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 text-charcoal border-gold/50 hover:border-gold"
                  onClick={() => {
                    const input = document.getElementById('image-input-create')
                    if (input) {
                      input.click()
                    }
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {selectedImage ? 'Change Image' : 'Select Image'}
                </Button>
                
                {selectedImage && (
                  <Button
                    variant="ghost"
                    onClick={handleRemoveImage}
                    className="text-charcoal hover:text-charcoal-muted"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              {!imagePreview && !selectedImage && (
                <p className="text-xs text-charcoal-muted">No image selected. Select an image to upload.</p>
              )}
            </div>
          </div>

          {/* Parish Admin Selection */}
          <div className="relative" ref={adminDropdownRef}>
            <Label className="text-charcoal-muted mb-2">Parish Admins</Label>
            <p className="text-sm text-charcoal-muted mb-3">Select users to assign as parish admins for this parish</p>
            
            {/* Selected Admins Display */}
            {selectedAdminUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedAdminUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center space-x-2 bg-gold/15 border border-gold/50 rounded px-3 py-1.5"
                  >
                    <span className="text-charcoal text-sm">
                      {user.email}
                      {user.name && ` (${user.name})`}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAdmin(user.id)}
                      className="text-charcoal hover:text-charcoal transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Searchable Dropdown */}
            {loadingUsers ? (
              <div className="flex items-center space-x-2 text-charcoal-muted">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading users...</span>
              </div>
            ) : users.length === 0 ? (
              <p className="text-sm text-charcoal-muted">No users available</p>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search users by email or name..."
                    value={adminSearchQuery}
                    onChange={(e) => {
                      setAdminSearchQuery(e.target.value)
                      setShowAdminDropdown(true)
                    }}
                    onFocus={() => setShowAdminDropdown(true)}
                    className="pr-10"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {showAdminDropdown ? (
                      <ChevronDown className="w-4 h-4 text-charcoal-muted rotate-180" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-charcoal-muted" />
                    )}
                  </div>
                </div>

                {/* Dropdown List */}
                {showAdminDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-surface-border rounded shadow-lg max-h-60 overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <div className="p-4 text-center text-charcoal-muted text-sm">
                        No users found
                      </div>
                    ) : (
                      <div className="py-1">
                        {filteredUsers.map((user) => {
                          const isSelected = selectedAdmins.includes(user.id)
                          return (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                handleToggleAdmin(user.id)
                                setAdminSearchQuery('')
                              }}
                              className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                isSelected
                                  ? 'bg-gold/15 text-charcoal'
                                  : 'text-charcoal hover:bg-surface-linen'
                              }`}
                            >
                              <div className="flex items-center space-x-2">
                                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                                  isSelected
                                    ? 'border-gold bg-gold'
                                    : 'border-gold/50'
                                }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium">{user.email}</div>
                                  {user.name && (
                                    <div className="text-xs text-charcoal-muted">{user.name}</div>
                                  )}
                                </div>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2 pt-4 border-t border-surface-border">
            <Button
              onClick={handleCreateParish}
              disabled={creating || uploadingImage}
              className="bg-gold text-black hover:bg-gold/90"
            >
              {(creating || uploadingImage) ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {uploadingImage ? 'Uploading Image...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Parish
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/app/admin/parishes')}
              disabled={creating}
              className="text-charcoal hover:text-charcoal-muted"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
